import type { NextPage } from 'next'
import Image from 'next/image'

import {
    activityStreamBigImage,
    activityStreamImage,
    commandLineShortcutsBlueBigImage,
    fieldGroupsCallContactSmallImage,
    fieldGroupsSelectFieldsImage,
    todayStackImage,
    marblesBumpySlideBigImage,
} from 'src/libs/assets'
import { PageLayout } from 'src/libs/layout'
import {
    IntroSection,
    PropositionSection,
    SecuritySection,
    ClosingSection,
    ThreeCardsSection,
    FaqSection,
    BannerWithCtaSection,
    RequestDemoButton,
} from 'src/libs/ui-shared'

const CustomerSuccessSolutionPage: NextPage = () => {
    return (
        <PageLayout isNavBarFixed>
            <div className="grid gap-40">
                <IntroSection
                    image={marblesBumpySlideBigImage}
                    imageAlt="Marbles being on a bumply slide"
                    label="Customer Success"
                    title="Build a clean work space"
                    description="Remove the clutter in Salesforce from other departments to stay better organized for your customers."
                >
                    <div className="grid place-content-center md:place-content-start">
                        <RequestDemoButton />
                    </div>
                </IntroSection>

                <PropositionSection
                    title="See all relevant information in one place"
                    description={[
                        'See every interaction with your accounts in a neat timeline: emails, tasks, events and notes.',
                        'Pin the most important interactions for future reference.',
                    ]}
                    image={
                        <Image
                            src={activityStreamBigImage}
                            alt="Activity stream"
                            layout="responsive"
                            placeholder="blur"
                        />
                    }
                    imageShadow
                    swapElementsWhenVertical
                />

                <PropositionSection
                    title="Keep track of your customer metrics"
                    description="Configure what metrics are important for you to keep track of account health. Cluster the information how you want to work with."
                    image={
                        <Image
                            src={fieldGroupsSelectFieldsImage}
                            alt="Selecting fields in field group"
                            layout="responsive"
                            placeholder="blur"
                        />
                    }
                    imageShadow
                />

                <PropositionSection
                    title="Stay on top of all tasks"
                    description="Set tasks for anything from anywhere and organize your days with everything planned out."
                    image={
                        <Image
                            src={commandLineShortcutsBlueBigImage}
                            alt="Command line shortcuts"
                            layout="responsive"
                            placeholder="blur"
                        />
                    }
                    imageShadow
                    swapElementsWhenVertical
                />

                <BannerWithCtaSection />

                <SecuritySection />

                <ThreeCardsSection
                    title="Simple to use"
                    description="You can see everything you want to work on in an instant. Laserfocus is your one system to organize your day AND manage all your customers."
                    cards={[
                        {
                            image: (
                                <Image
                                    src={todayStackImage}
                                    alt="Daily overview in Laserfocus app"
                                    layout="responsive"
                                    placeholder="blur"
                                />
                            ),
                            title: 'Daily overview',
                            description:
                                'See all your tasks, reminders and events. Reschedule them with one-click to save you time.',
                            backgroundColor: 'blue',
                        },
                        {
                            image: (
                                <Image
                                    src={fieldGroupsCallContactSmallImage}
                                    alt="Call contacts from Laserfocus"
                                    layout="responsive"
                                    placeholder="blur"
                                />
                            ),
                            title: 'Connect with your tools',
                            description:
                                'You can call, email, set tasks and schedule directly out of Laserfocus. Everything is connected with your tools and Salesforce.',
                            backgroundColor: 'purple',
                        },
                        {
                            image: (
                                <Image
                                    src={activityStreamImage}
                                    alt="Acitivty stream"
                                    layout="responsive"
                                    placeholder="blur"
                                />
                            ),
                            title: 'Be always prepared',
                            description:
                                'All activities are shown readable in chronologically in Laserfocus. See your notes, other notes and all information.',
                            backgroundColor: 'red',
                        },
                    ]}
                />

                <FaqSection
                    title="Frequently asked questions"
                    description="Laserfocus helps you become more productive by offering you all the tools you need to close deals with confidence."
                    elements={[
                        {
                            question: 'How can I try Laserfocus for myself?',
                            answer: 'Connect with one click via Salesforce Oauth and start using Laserfocus. Laserfocus sits on top of your Salesforce data, so there is no setup or installation required.',
                        },
                        {
                            question: 'What does Laserfocus cost?',
                            answer: 'It’s free for individuals and small teams trying out Laserfocus.',
                        },
                        {
                            question: 'Do I need my Salesforce Admin for setup?',
                            answer: [
                                'Nope. Laserfocus doesn’t require any setup or installation.',
                                'You securely log in and connect via Salesforce Oauth with Laserfocus and can start using it instantly if your Salesforce license has API access.',
                            ],
                        },
                        {
                            question: 'How long does the setup need?',
                            answer: 'Like everything in Laserfocus: it’s superfast. One click and you are ready. Means 30 seconds.',
                        },
                        {
                            question: 'Are you changing my Salesforce setup?',
                            answer: [
                                'No. Salesforce is defined and setup by your company, we don’t interact with your workflows in Salesforce or create any fields.',
                                'Everything stays untouched in your Salesforce instance.',
                            ],
                        },
                        {
                            question: 'How much time can Laserfocus save me?',
                            answer: 'Glad you asked. Just think about how much time you spend in Salesforce per day and cut that by 80%.',
                        },
                    ]}
                />

                <ClosingSection
                    title="Start saving your life time now"
                    description="Laserfocus is build to save you time in your day-to-day. Start now and get more out of your day."
                />
            </div>
        </PageLayout>
    )
}

export default CustomerSuccessSolutionPage
